import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { SubscriptionWebmailDto, WebmailSettingsDto } from '@cpm/shared';
import { api } from '../api/client.js';
import { useMe } from '../api/auth.js';
import { Badge, Button, Card, CardHeader, ErrorNote, Input, Label } from './ui.js';

export function WebmailSettingsCard() {
  const [secret, setSecret] = useState('');
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['mail', 'webmail-settings'],
    queryFn: () => api.get<WebmailSettingsDto>('/api/mail/webmail/settings'),
  });
  const save = useMutation({
    mutationFn: () => api.put('/api/mail/webmail/settings', { secret }),
    onSuccess: () => {
      setSecret('');
      void queryClient.invalidateQueries({ queryKey: ['mail'] });
    },
  });
  return (
    <Card>
      <CardHeader
        title="Shared webmail"
        description="Connect Roundcube once, then enable webmail independently on each hosting account."
      />
      <form
        className="space-y-3 px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <p className="text-sm">
          {status.data?.configured
            ? 'Integration secret saved. Accounts remain controlled individually.'
            : 'Save the same integration secret configured in Roundcube. Every account starts with webmail disabled.'}
        </p>
        <div>
          <Label htmlFor="webmail-secret">Integration secret</Label>
          <Input
            id="webmail-secret"
            type="password"
            autoComplete="new-password"
            minLength={32}
            maxLength={128}
            required
            value={secret}
            onChange={(event) => setSecret(event.target.value)}
          />
        </div>
        <p className="text-sm text-slate-500">
          Changing this secret also requires updating Roundcube. Mailbox passwords are separate.
        </p>
        <ErrorNote error={status.error ?? save.error} />
        <Button type="submit" disabled={save.isPending || secret.length < 32}>
          Save integration secret
        </Button>
      </form>
    </Card>
  );
}

export function SubscriptionWebmailPanel({ subscriptionId }: { subscriptionId: string }) {
  const me = useMe();
  const queryClient = useQueryClient();
  const key = ['mail', 'webmail', subscriptionId];
  const state = useQuery({
    queryKey: key,
    queryFn: () =>
      api.get<SubscriptionWebmailDto>(`/api/mail/subscriptions/${subscriptionId}/webmail`),
  });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) =>
      api.put(`/api/mail/subscriptions/${subscriptionId}/webmail`, { enabled }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
  return (
    <div className="space-y-2 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
      <div className="flex items-center justify-between gap-3">
        <span className="font-medium">
          Webmail{' '}
          <Badge tone={state.data?.enabled ? 'green' : 'gray'}>
            {state.data?.enabled ? 'enabled' : 'disabled'}
          </Badge>
        </span>
        {me.data?.role === 'admin' && state.data && (
          <Button
            variant="secondary"
            disabled={toggle.isPending || (!state.data.configured && !state.data.enabled)}
            onClick={() => toggle.mutate(!state.data.enabled)}
          >
            {state.data.enabled ? 'Disable webmail' : 'Enable webmail'}
          </Button>
        )}
      </div>
      <p className="text-sm text-slate-500">
        Applies only to this hosting account. Disabling blocks new logins and existing sessions on
        their next request; saved profiles and identities are retained.
      </p>
      {state.data && !state.data.configured && (
        <p className="text-sm text-slate-500">
          An administrator must connect shared webmail on the Mail page first.
        </p>
      )}
      <ErrorNote error={state.error ?? toggle.error} />
    </div>
  );
}
